/**
 * FindReplaceModal \u2014 Global Find & Replace across all content keys.
 * Opens via Ctrl+H from ContentEditToggle.
 *
 * Enhanced: searches BOTH DOM elements with [data-content-key] AND
 * all stored overrides via getAllOverrides(), so even off-screen
 * content is included. Enter/Shift+Enter navigate through matches.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Search, Replace, X, ChevronDown, ChevronUp, AlertCircle, CaseSensitive, WholeWord } from "lucide-react";
import { useContentEditingSafe } from "./ContentEditingContext";
import { toast } from "sonner";

const btnReset =
  "appearance-none border-none font-inherit cursor-pointer p-0 m-0 leading-normal";

interface MatchResult {
  key: string;
  value: string;
  /** Indices in value where the search term appears */
  indices: number[];
  /** Where the value came from */
  source: "dom" | "override";
}

export function FindReplaceModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const ctx = useContentEditingSafe();
  const [searchTerm, setSearchTerm] = useState("");
  const [replaceTerm, setReplaceTerm] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [activeMatchIdx, setActiveMatchIdx] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 100);
      setSearchTerm("");
      setReplaceTerm("");
      setActiveMatchIdx(0);
    }
  }, [isOpen]);

  // Keyboard: Escape to close, Enter to navigate
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      // Enter \u2192 next match, Shift+Enter \u2192 prev match
      if (e.key === "Enter" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        if (e.shiftKey) {
          setActiveMatchIdx((prev) => (prev <= 0 ? Math.max(totalMatchesRef.current - 1, 0) : prev - 1));
        } else {
          setActiveMatchIdx((prev) => (prev >= totalMatchesRef.current - 1 ? 0 : prev + 1));
        }
      }
    };
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [isOpen, onClose]);

  // Ref for totalMatches to use in keyboard handler without stale closures
  const totalMatchesRef = useRef(0);

  // Find matches across all content
  const matches: MatchResult[] = useMemo(() => {
    if (!ctx || !searchTerm.trim()) return [];
    const results: MatchResult[] = [];
    const seen = new Set<string>();

    // Build the search function
    const findIndices = (text: string, term: string): number[] => {
      const searchIn = caseSensitive ? text : text.toLowerCase();
      const searchFor = caseSensitive ? term : term.toLowerCase();
      const indices: number[] = [];
      let pos = 0;
      while (true) {
        const idx = searchIn.indexOf(searchFor, pos);
        if (idx === -1) break;
        if (wholeWord) {
          const before = idx > 0 ? searchIn[idx - 1] : " ";
          const after = idx + searchFor.length < searchIn.length ? searchIn[idx + searchFor.length] : " ";
          const wordBoundary = /[\s\u200B\u00A0.,;:!?\-()[\]{}'"\/\\<>|@#$%^&*+=~`]|^$/;
          if (wordBoundary.test(before) && wordBoundary.test(after)) {
            indices.push(idx);
          }
        } else {
          indices.push(idx);
        }
        pos = idx + 1;
      }
      return indices;
    };

    // 1. Scan DOM for [data-content-key] elements
    const editables = document.querySelectorAll("[data-content-key]");
    editables.forEach((el) => {
      const key = el.getAttribute("data-content-key");
      if (!key || seen.has(key)) return;
      seen.add(key);
      const text = el.textContent || "";
      const indices = findIndices(text, searchTerm);
      if (indices.length > 0) {
        results.push({ key, value: text, indices, source: "dom" });
      }
    });

    // 2. Scan all stored overrides (catches off-screen content)
    const allOverrides = ctx.getAllOverrides();
    for (const [key, value] of Object.entries(allOverrides)) {
      if (seen.has(key)) continue;
      // Skip system keys
      if (key.startsWith("__")) continue;
      seen.add(key);
      const indices = findIndices(value, searchTerm);
      if (indices.length > 0) {
        results.push({ key, value, indices, source: "override" });
      }
    }

    return results;
  }, [ctx, searchTerm, caseSensitive, wholeWord]);

  const totalMatches = matches.reduce((sum, m) => sum + m.indices.length, 0);
  totalMatchesRef.current = totalMatches;

  // Replace single match
  const replaceOne = useCallback(() => {
    if (!ctx || matches.length === 0 || !searchTerm) return;

    let counter = 0;
    for (const match of matches) {
      for (let i = 0; i < match.indices.length; i++) {
        if (counter === activeMatchIdx) {
          const before = match.value.substring(0, match.indices[i]);
          const after = match.value.substring(match.indices[i] + searchTerm.length);
          const newValue = before + replaceTerm + after;
          ctx.saveContent(match.key, newValue);
          toast.success("\u05D4\u05D5\u05D7\u05DC\u05E3 1 \u05EA\u05D5\u05E6\u05D0\u05D4");
          if (activeMatchIdx >= totalMatches - 1) {
            setActiveMatchIdx(0);
          }
          return;
        }
        counter++;
      }
    }
  }, [ctx, matches, activeMatchIdx, searchTerm, replaceTerm, totalMatches]);

  // Replace all matches
  const replaceAll = useCallback(() => {
    if (!ctx || matches.length === 0 || !searchTerm) return;

    let totalReplaced = 0;
    for (const match of matches) {
      let newValue = match.value;
      if (caseSensitive) {
        newValue = newValue.split(searchTerm).join(replaceTerm);
      } else {
        const regex = new RegExp(searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
        newValue = newValue.replace(regex, replaceTerm);
      }
      if (newValue !== match.value) {
        ctx.saveContent(match.key, newValue);
        totalReplaced += match.indices.length;
      }
    }

    toast.success(`\u05D4\u05D5\u05D7\u05DC\u05E4\u05D5 ${totalReplaced} \u05EA\u05D5\u05E6\u05D0\u05D5\u05EA`);
    setActiveMatchIdx(0);
  }, [ctx, matches, searchTerm, replaceTerm, caseSensitive]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9000] flex items-start justify-center pt-24"
      dir="rtl"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" />

      {/* Modal */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-lg mx-4"
        style={{ animation: "frFadeIn 0.2s ease-out" }}
      >
        {/* Header */}
        <div
          className="px-5 py-3.5 flex items-center justify-between"
          style={{
            background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
          }}
        >
          <div className="flex items-center gap-2.5">
            <Replace className="w-4 h-4 text-white/80" />
            <span className="text-white text-sm" style={{ fontWeight: 700 }}>
              {"\u05D7\u05D9\u05E4\u05D5\u05E9 \u05D5\u05D7\u05DC\u05E4\u05D4 \u05D2\u05DC\u05D5\u05D1\u05DC\u05D9"}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`${btnReset} text-white/50 hover:text-white`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search / Replace inputs */}
        <div className="p-5 space-y-3">
          {/* Search field */}
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
              <input
                ref={searchRef}
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setActiveMatchIdx(0);
                }}
                placeholder={"\u05D7\u05E4\u05E9\u05D5 \u05D8\u05E7\u05E1\u05D8..."}
                className="w-full pr-10 pl-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-300"
                style={{ fontFamily: "var(--font-family)" }}
              />
            </div>
            {/* Case Sensitive toggle */}
            <button
              type="button"
              onClick={() => setCaseSensitive(!caseSensitive)}
              className={`${btnReset} p-2.5 rounded-xl border text-xs ${
                caseSensitive
                  ? "bg-indigo-50 border-indigo-300 text-indigo-600"
                  : "bg-white border-gray-200 text-gray-400 hover:bg-gray-50"
              }`}
              title={"\u05E8\u05D2\u05D9\u05E9\u05D5\u05EA \u05DC\u05E8\u05D9\u05E9\u05D9\u05D5\u05EA/\u05E7\u05D8\u05E0\u05D5\u05EA"}
            >
              <CaseSensitive className="w-4 h-4" />
            </button>
            {/* Whole Word toggle */}
            <button
              type="button"
              onClick={() => setWholeWord(!wholeWord)}
              className={`${btnReset} p-2.5 rounded-xl border text-xs ${
                wholeWord
                  ? "bg-indigo-50 border-indigo-300 text-indigo-600"
                  : "bg-white border-gray-200 text-gray-400 hover:bg-gray-50"
              }`}
              title={"\u05DE\u05D9\u05DC\u05D4 \u05E9\u05DC\u05DE\u05D4"}
            >
              <WholeWord className="w-4 h-4" />
            </button>
          </div>

          {/* Replace field */}
          <div className="relative">
            <Replace className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
            <input
              type="text"
              value={replaceTerm}
              onChange={(e) => setReplaceTerm(e.target.value)}
              placeholder={"\u05D4\u05D7\u05DC\u05D9\u05E4\u05D5 \u05D1..."}
              className="w-full pr-10 pl-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-300"
              style={{ fontFamily: "var(--font-family)" }}
            />
          </div>

          {/* Results count + navigate */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">
              {searchTerm.trim()
                ? totalMatches > 0
                  ? `${totalMatches} \u05EA\u05D5\u05E6\u05D0\u05D5\u05EA \u05D1-${matches.length} \u05E9\u05D3\u05D5\u05EA`
                  : "\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0\u05D5 \u05EA\u05D5\u05E6\u05D0\u05D5\u05EA"
                : "\u05D4\u05E7\u05DC\u05D9\u05D3\u05D5 \u05D8\u05E7\u05E1\u05D8 \u05DC\u05D7\u05D9\u05E4\u05D5\u05E9 \u00B7 Enter \u05DC\u05E0\u05D9\u05D5\u05D5\u05D8"}
            </span>
            {totalMatches > 1 && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500" style={{ fontWeight: 500 }}>
                  {activeMatchIdx + 1}/{totalMatches}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setActiveMatchIdx((prev) =>
                      prev <= 0 ? totalMatches - 1 : prev - 1,
                    )
                  }
                  className={`${btnReset} p-1 rounded hover:bg-gray-100`}
                >
                  <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setActiveMatchIdx((prev) =>
                      prev >= totalMatches - 1 ? 0 : prev + 1,
                    )
                  }
                  className={`${btnReset} p-1 rounded hover:bg-gray-100`}
                >
                  <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Match list */}
        {searchTerm.trim() && matches.length > 0 && (
          <div className="px-5 pb-3 max-h-48 overflow-y-auto border-t border-gray-100">
            {matches.map((m) => {
              const label = m.key
                .replace(/^.*?:/, "")
                .replace(/:/g, " \u203A ")
                .replace(/-/g, " ");
              return (
                <div
                  key={m.key}
                  className="py-2 border-b border-gray-50 last:border-0"
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="text-[10px] text-gray-400 truncate flex-1" style={{ fontWeight: 600 }}>
                      {label}
                    </div>
                    {m.source === "override" && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-500 border border-purple-100" style={{ fontWeight: 500 }}>
                        {"\u05DE\u05D0\u05D5\u05D7\u05E1\u05DF"}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-600 leading-relaxed line-clamp-2" dir="auto">
                    {highlightMatches(m.value, searchTerm, caseSensitive)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {searchTerm.trim() && matches.length === 0 && (
          <div className="px-5 pb-5 flex items-center gap-2 text-gray-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{"\u05D4\u05D8\u05E7\u05E1\u05D8 \u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0 \u05D1\u05D0\u05E3 \u05E9\u05D3\u05D4 \u05EA\u05D5\u05DB\u05DF \u05D1\u05EA\u05D1\u05E0\u05D9\u05EA"}</span>
          </div>
        )}

        {/* Action buttons */}
        <div
          className="px-5 py-3.5 flex items-center gap-2 border-t border-gray-100"
          style={{ background: "#f9fafb" }}
        >
          <button
            type="button"
            onClick={replaceOne}
            disabled={totalMatches === 0 || !replaceTerm}
            className={`${btnReset} px-4 py-2 rounded-xl text-xs shadow-sm disabled:opacity-40`}
            style={{
              fontWeight: 600,
              background: totalMatches > 0 && replaceTerm ? "#4f46e5" : "#e5e7eb",
              color: totalMatches > 0 && replaceTerm ? "#fff" : "#9ca3af",
            }}
          >
            {"\u05D4\u05D7\u05DC\u05E3 \u05D0\u05D7\u05D3"}
          </button>
          <button
            type="button"
            onClick={replaceAll}
            disabled={totalMatches === 0 || !replaceTerm}
            className={`${btnReset} px-4 py-2 rounded-xl text-xs shadow-sm disabled:opacity-40`}
            style={{
              fontWeight: 600,
              background: totalMatches > 0 && replaceTerm ? "#7c3aed" : "#e5e7eb",
              color: totalMatches > 0 && replaceTerm ? "#fff" : "#9ca3af",
            }}
          >
            {`\u05D4\u05D7\u05DC\u05E3 \u05D4\u05DB\u05DC (${totalMatches})`}
          </button>
          <span className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            className={`${btnReset} px-4 py-2 rounded-xl text-xs bg-gray-100 text-gray-500 hover:bg-gray-200`}
            style={{ fontWeight: 500 }}
          >
            {"\u05E1\u05D2\u05D5\u05E8"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes frFadeIn {
          from { opacity: 0; transform: translateY(-12px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

/** Highlight search matches inside text */
function highlightMatches(
  text: string,
  term: string,
  caseSensitive: boolean,
): React.ReactNode {
  if (!term) return text;
  const regex = new RegExp(
    `(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    caseSensitive ? "g" : "gi",
  );
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="bg-amber-200/70 rounded px-0.5">
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}